import React, { useEffect, useMemo, useState } from 'react';
import { Brush, Sparkles, ImageIcon, Check } from 'lucide-react';
import { useT } from '../i18n';

const STAGE_DURATION_MS = 15000;

export default function FinalizeLoader() {
  const t = useT();
  // Step labels rotate every STAGE_DURATION_MS so the user gets a sense of
  // progress even though the server doesn't stream per-scene updates yet. We
  // stop short of "complete" until the parent unmounts us — the real signal
  // is the BookViewer rendering.
  const STAGES = useMemo(() => [
    { icon: ImageIcon, label: t.finalizeStage1 },
    { icon: Brush,     label: t.finalizeStage2 },
    { icon: Sparkles,  label: t.finalizeStage3 },
  ], [t]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((x) => Math.min(x + 1, STAGES.length - 1));
    }, STAGE_DURATION_MS);
    return () => clearInterval(id);
  }, []);

  // Cap at 95% so the bar never claims completion before the parent advances.
  const progress = Math.min(((active + 1) / STAGES.length) * 100, 95);

  return (
    <div className="story-loader-pro" role="status" aria-live="polite">
      <div className="story-loader-orb">
        <div className="story-loader-orb-glow" />
        <div className="story-loader-orb-ring" />
        <div className="story-loader-orb-ring story-loader-orb-ring-2" />
        <Brush size={40} className="story-loader-orb-icon" />
      </div>

      <h2 className="story-loader-title">{t.finalizeLoaderTitle}</h2>
      <p className="story-loader-subtitle">{t.finalizeLoaderSubtitle}</p>

      <ul className="story-loader-steps">
        {STAGES.map((stage, i) => {
          const Icon = stage.icon;
          const state = i < active ? 'done' : i === active ? 'active' : 'pending';
          return (
            <li key={i} className={`story-loader-step story-loader-step-${state}`}>
              <span className="story-loader-step-icon">
                {state === 'done' ? <Check size={18} /> : <Icon size={18} />}
              </span>
              <span className="story-loader-step-label">{stage.label}</span>
              {state === 'active' && <span className="story-loader-step-spinner" />}
            </li>
          );
        })}
      </ul>

      <div className="story-loader-progress">
        <div className="story-loader-progress-bar" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}
